import { useState, useEffect } from 'react';

import { usePostHog } from 'posthog-js/react';

import { functions } from "../firebase";
import { httpsCallable } from "firebase/functions";
import { auth } from "../firebase";
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, onAuthStateChanged, sendPasswordResetEmail } from "firebase/auth";
import { db } from "../firebase";
import { doc, setDoc, updateDoc, collection, getDocs } from "firebase/firestore";

import creelLogo from '../assets/logo.png';

import SignIn from './SignIn';
import Hub from './Hub';

const App = () => {
  const posthog = usePostHog()

  const [uid, setUid] = useState(null);
  const [userZoomInfo, setUserZoomInfo] = useState(null);
  const [signedIn, setSignedIn] = useState(null);
  const [signingUp, setSigningUp] = useState(false);
  const [myLists, setMyLists] = useState([]);
  const [overdues, setOverdues] = useState([]);
  const [callbacks, setCallbacks] = useState([]);
  const [upcoming, setUpcoming] = useState([]);

  const [selectedList, setSelectedList] = useState(() => {
    return localStorage.getItem("CREEL_DIALER_SELECTED_LIST") || null;
  });

  const handleSignOut = async () => {
    await signOut(auth);
    setSignedIn(false);
  }

  const handleSignIn = async (e) => {
    e.preventDefault();
    const email = e.target.email.value;
    const password = e.target.password.value;
    try {
      const userCredentials = await signInWithEmailAndPassword(auth, email, password);
      if (userCredentials) {
        const uid = userCredentials.user.uid;
        setUid(uid);
        setSignedIn(true);
        posthog?.identify(uid, { email: email });
        posthog?.capture('sign-in', { user_id: uid, user_email: email });
      }
    } catch (error) {
      alert('You entered an incorrect email or password. Please try again.');
    }
  }

  const handleSignUp = async (e) => {
    e.preventDefault();
    const email = userZoomInfo.email;
    const password = e.target.password.value;
    const confirmPassword = e.target.confirm.value;
    if (password === confirmPassword) {
      const userCredentials = await createUserWithEmailAndPassword(auth, email, password);
      if (userCredentials) {
        const user = userCredentials.user;
        const userDocRef = doc(db, "autodialer", "users", "ids", user.uid);
        await setDoc(userDocRef, {
          zoomInfo: userZoomInfo,
          zoomEvents: {},
        });
        const subDocRef = doc(db, "paypalSubscriptions", user.uid);
        await setDoc(subDocRef, { subStatus: "new" }, { merge: true });
        const mapDocRef = doc(db, "autodialer", "users");
        await updateDoc(mapDocRef, {
          [`zoomIdMap.${userZoomInfo.id}`]: user.uid
        });
        window.location.assign('/');
        setSigningUp(false);
        setSignedIn(true);
        posthog?.identify(uid, { email: email });
        posthog?.capture('sign-up', { user_id: uid, user_email: email });
      }
    } else {
      alert('The passwords you entered do not match. Please try again.');
    }
  }

  const handleResetPassword = async () => {
    const email = window.prompt('Please enter your email');
    try {
      await sendPasswordResetEmail(auth, email);
      alert(`A password reset email was sent to: ${email}`);
    } catch (error) {
      if (error.code === 'auth/missing-email') {
        alert('No email was entered.');
      } else if (error.code === 'auth/invalid-email') {
        alert('The email you entered is not valid. Please enter the email associated with the Zoom account that you used to sign up.');
      }
    }
  }

  useEffect(() => {
    localStorage.setItem("CREEL_DIALER_SELECTED_LIST", selectedList);
  }, [selectedList]);

  useEffect(() => {
    const fetchLists = async (uid) => {
      const listsColRef = collection(db, "autodialer", "users", "ids", uid, "lists");
      const listsSnapshot = await getDocs(listsColRef);
      const myListsTmp = [];
      listsSnapshot.forEach((doc) => {
        myListsTmp.push({ id: doc.id, ...doc.data() });
      });
      setMyLists(myListsTmp);
      if (selectedList === null) {
        setSelectedList(myListsTmp[0].id);
      }
    }

    const fetchCallbacks = async (uid) => {
      const alertSound = new Audio('https://smartdial-files.netlify.app/alert.wav');
      const callbacksColRef = collection(db, 'autodialer', 'users', 'ids', uid, 'callbacks');
      const callbackColSnap = await getDocs(callbacksColRef);
      const upcomingTmp = [];
      const callbacksTmp = [];
      const overduesTmp = [];
      const now = new Date();
      const eightHours = 8 * 60 * 60 * 1000;
      const fifteenMinutes = 15 * 60 * 1000;
      callbackColSnap.forEach(async (contact) => {
        const dueDate = new Date(contact.data().dueDate.seconds * 1000);
        const timeDelta = dueDate.getTime() - now.getTime();
        if (timeDelta < 0) {
          overduesTmp.push({ id: contact.id, ...contact.data() });
        } else if (timeDelta < eightHours) {
          alertSound.addEventListener('canplaythrough', () => {
            setTimeout(() => {
              alertSound.play();
              alert(`Your call with ${contact.data().name}: ${contact.data().phone} is in ${Math.floor(timeDelta / 1000 / 60)}min`);
            }, timeDelta - fifteenMinutes);
          }, { once: true });
          callbacksTmp.push({ id: contact.id, ...contact.data() });
        } else {
          upcomingTmp.push({ id: contact.id, ...contact.data() });
        }
      });
      setCallbacks(callbacksTmp.sort((a, b) => a.dueDate.seconds - b.dueDate.seconds));
      setUpcoming(upcomingTmp.sort((a, b) => a.dueDate.seconds - b.dueDate.seconds));
      setOverdues(overduesTmp.sort((a, b) => a.dueDate.seconds - b.dueDate.seconds));
    }

    onAuthStateChanged(auth, (user) => {
      if (user) {
        setUid(user.uid);
        fetchLists(user.uid);
        fetchCallbacks(user.uid);
        setSignedIn(true);
      } else {
        setSignedIn(false);
      }
    });

    // eslint wants selectedList in dep array
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const zoomSignUp = httpsCallable(functions, 'zoomSignUp');

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    if (code) {
      setSigningUp(true);
      zoomSignUp({ code: code })
        .then((result) => {
          const info = result.data;
          setUserZoomInfo(info);
        })
        .catch((error) => {
          console.error('Error during Zoom sign up:', error);
        });
    }
  }, []);

  return (
    <div className='app'>
      <div className="branding">
        <img src={creelLogo} style={{ width: '9.5rem' }} alt='creel logo' />
        <div style={{ color: 'var(--neutral-400)' }}>Version: 1.0</div>
      </div>
      {
        signedIn === null ?
          <div>Loading...</div>
          : !signedIn ?
            <SignIn
              handleSignIn={handleSignIn}
              handleSignUp={handleSignUp}
              signingUp={signingUp}
              userZoomInfo={userZoomInfo}
              handleResetPassword={handleResetPassword}
            />
            :
            <Hub
              handleSignOut={handleSignOut}
              uid={uid}
              myLists={myLists}
              setMyLists={setMyLists}
              selectedList={selectedList}
              setSelectedList={setSelectedList}
              callbacks={callbacks}
              setCallbacks={setCallbacks}
              overdues={overdues}
              upcoming={upcoming}
              setUpcoming={setUpcoming}
              setOverdues={setOverdues}
              handleResetPassword={handleResetPassword}
            />
      }
    </div>
  );
}

export default App;